import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockingConfirm } from './useBlockingConfirm';
import {
  pickLocalized,
  flattenPaperQuestions,
  flattenSectionQuestions,
  getSectionById,
  globalQuestionIndexToSection,
  countAnsweredInPaper,
  findPassageInSection,
  paperUsesSectionMenu,
  getPrimarySection,
  getSectionsWithQuestions,
  getSectionIndex,
  getNextSection,
  APP_MAIN_CONTENT_ID
} from '../utils/testUtils';
import {
  fetchTestProgress,
  saveTestProgress,
  clearTestProgress,
  clearAllAbilityProgress,
  submitTest
} from '../services/testService';
import {
  getAbilitySuiteAttemptId,
  ensureAbilitySuiteAttemptId,
  startNewAbilitySuiteAttempt,
  filterProgressByAttemptId
} from '../utils/abilitySuiteAttempt';
import {
  normalizeQuestionTimings,
  snapshotQuestionTimings,
  addElapsedToTimings
} from '../utils/questionTimingUtils';
import { buildChapterResult } from '../utils/chapterResultUtils';
import { getPlayableAbilityPapers } from '../data/abilityPapers';

const PAPER_TYPE = 'ability';

const hasToken = () => Boolean(localStorage.getItem('token'));

function digitFromKeyboardEvent(e) {
  if (e.key.length === 1 && e.key >= '1' && e.key <= '9') {
    return e.key.charCodeAt(0) - 48;
  }
  const m = /^Numpad([1-9])$/.exec(e.code || '');
  return m ? parseInt(m[1], 10) : null;
}

function buildProgressPayload({
  answers,
  questionIndex,
  remainingSeconds,
  resumePhase,
  activeSectionId,
  questionTimings = {}
}) {
  const attemptId = getAbilitySuiteAttemptId();
  return {
    paperType: PAPER_TYPE,
    answers,
    questionIndex,
    remainingSeconds,
    resumePhase,
    activeSectionId: activeSectionId || null,
    questionTimings,
    ...(attemptId ? { attemptId } : {})
  };
}

function timingsFromResumeDoc(resumeDoc) {
  return normalizeQuestionTimings(resumeDoc?.questionTimings);
}

export function useTestPipeline() {
  const { t, i18n } = useTranslation();
  const { confirmAsync, isConfirmOpen, ConfirmDialog } = useBlockingConfirm();
  const lang = i18n.language || 'en';

  const [activePaper, setActivePaper] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [pendingResume, setPendingResume] = useState(null);
  const [phase, setPhase] = useState('suiteCover');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [questionTimings, setQuestionTimings] = useState({});
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [timerExpired, setTimerExpired] = useState(false);
  const [chapterResult, setChapterResult] = useState(null);
  const [progressBySlug, setProgressBySlug] = useState({});

  const answersRef = useRef(answers);
  const questionTimingsRef = useRef(questionTimings);
  const activePaperRef = useRef(activePaper);
  const remainingSecondsRef = useRef(remainingSeconds);
  const trackedQuestionIdRef = useRef(null);
  const questionStartedAtRef = useRef(null);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  useEffect(() => {
    questionTimingsRef.current = questionTimings;
  }, [questionTimings]);
  useEffect(() => {
    activePaperRef.current = activePaper;
  }, [activePaper]);
  useEffect(() => {
    remainingSecondsRef.current = remainingSeconds;
  }, [remainingSeconds]);

  const flushActiveQuestionTiming = useCallback(() => {
    const qid = trackedQuestionIdRef.current;
    const started = questionStartedAtRef.current;
    if (!qid || started == null) {
      trackedQuestionIdRef.current = null;
      questionStartedAtRef.current = null;
      return;
    }
    setQuestionTimings((prev) => {
      const next = addElapsedToTimings(prev, qid, started);
      questionTimingsRef.current = next;
      return next;
    });
    trackedQuestionIdRef.current = null;
    questionStartedAtRef.current = null;
  }, []);

  const startTrackingQuestion = useCallback((questionId) => {
    if (!questionId) return;
    trackedQuestionIdRef.current = questionId;
    questionStartedAtRef.current = Date.now();
  }, []);

  /** Commit in-progress question time and return full timings map for submit. */
  const flushAndGetTimings = useCallback(() => {
    const qid = trackedQuestionIdRef.current;
    const started = questionStartedAtRef.current;
    let next = { ...questionTimingsRef.current };
    if (qid && started != null) {
      next = addElapsedToTimings(next, qid, started);
      questionTimingsRef.current = next;
      setQuestionTimings(next);
    }
    trackedQuestionIdRef.current = null;
    questionStartedAtRef.current = null;
    return next;
  }, []);

  const snapshotTimingsForSave = useCallback(() => {
    return snapshotQuestionTimings(
      questionTimingsRef.current,
      trackedQuestionIdRef.current,
      questionStartedAtRef.current
    );
  }, []);

  const flushAndGetTimingsRef = useRef(flushAndGetTimings);
  flushAndGetTimingsRef.current = flushAndGetTimings;
  const finalizeChapterSubmissionRef = useRef(async () => {});

  const loadProgressList = useCallback(async () => {
    if (!hasToken()) {
      setProgressBySlug({});
      return;
    }
    try {
      const attemptId = getAbilitySuiteAttemptId();
      const list = await fetchTestProgress(PAPER_TYPE, attemptId || undefined);
      const filtered = filterProgressByAttemptId(list, attemptId);
      const map = {};
      for (const row of filtered) {
        map[row.paperSlug] = row;
      }
      setProgressBySlug(map);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const finalizeChapterSubmission = useCallback(
    async (completionReason) => {
      const paper = activePaperRef.current;
      if (!paper) return;
      const timings =
        typeof flushAndGetTimingsRef.current === 'function'
          ? flushAndGetTimingsRef.current()
          : questionTimingsRef.current;
      let result = null;
      if (hasToken()) {
        try {
          const attemptId = getAbilitySuiteAttemptId();
          const res = await submitTest({
            paperSlug: paper.slug,
            paperType: PAPER_TYPE,
            answers: answersRef.current,
            questionTimings: timings,
            completionReason,
            ...(attemptId ? { attemptId } : {})
          });
          result = buildChapterResult(paper.slug, res);
          await loadProgressList();
        } catch (e) {
          console.error(e);
        }
      }
      setChapterResult(result);
      setActiveSectionId(null);
      setPhase('complete');
    },
    [loadProgressList]
  );

  finalizeChapterSubmissionRef.current = finalizeChapterSubmission;

  const activeSection = useMemo(
    () => (activePaper && activeSectionId ? getSectionById(activePaper, activeSectionId) : null),
    [activePaper, activeSectionId]
  );

  const flatItems = useMemo(() => {
    if (!activePaper) return [];
    if (phase === 'questions' && activeSection) {
      return flattenSectionQuestions(activeSection);
    }
    return flattenPaperQuestions(activePaper);
  }, [activePaper, activeSection, phase]);

  const allPaperItems = useMemo(
    () => (activePaper ? flattenPaperQuestions(activePaper) : []),
    [activePaper]
  );

  const totalQuestions = flatItems.length;
  const totalPaperQuestions = allPaperItems.length;
  const currentItem = flatItems[questionIndex];
  const currentQuestionId = currentItem?.question?.id ?? null;
  const prevItem = questionIndex > 0 ? flatItems[questionIndex - 1] : null;
  /** 0-based index within the whole ability paper (not the active subsection). */
  const paperQuestionIndex = useMemo(() => {
    if (!currentQuestionId) return 0;
    const idx = allPaperItems.findIndex(({ question }) => question.id === currentQuestionId);
    return idx >= 0 ? idx : 0;
  }, [allPaperItems, currentQuestionId]);

  useEffect(() => {
    if (phase !== 'questions' || !currentQuestionId) {
      flushActiveQuestionTiming();
      return;
    }
    if (trackedQuestionIdRef.current && trackedQuestionIdRef.current !== currentQuestionId) {
      flushActiveQuestionTiming();
    }
    if (trackedQuestionIdRef.current !== currentQuestionId) {
      startTrackingQuestion(currentQuestionId);
    }
  }, [
    phase,
    currentQuestionId,
    questionIndex,
    activeSectionId,
    flushActiveQuestionTiming,
    startTrackingQuestion
  ]);

  const answeredInPaper = useMemo(
    () => (activePaper ? countAnsweredInPaper(activePaper, answers) : 0),
    [activePaper, answers]
  );

  const allQuestionsAnswered =
    totalPaperQuestions > 0 && answeredInPaper >= totalPaperQuestions;

  useEffect(() => {
    loadProgressList();
  }, [loadProgressList]);

  const persistProgress = useCallback(
    async (paperSlug, payload) => {
      if (!hasToken() || !paperSlug) return;
      try {
        await saveTestProgress(paperSlug, payload);
        await loadProgressList();
      } catch (e) {
        console.error(e);
      }
    },
    [loadProgressList]
  );

  const resetSession = useCallback(() => {
    flushActiveQuestionTiming();
    setActivePaper(null);
    setActiveSectionId(null);
    setPendingResume(null);
    setPhase('menu');
    setQuestionIndex(0);
    setAnswers({});
    setQuestionTimings({});
    questionTimingsRef.current = {};
    setRemainingSeconds(0);
    setTimerExpired(false);
    setChapterResult(null);
    loadProgressList();
  }, [loadProgressList, flushActiveQuestionTiming]);

  const defaultLimit = (paper) =>
    paper && typeof paper.timeLimitSeconds === 'number' && paper.timeLimitSeconds > 0
      ? paper.timeLimitSeconds
      : 3600;

  const firstUnansweredInSection = useCallback(
    (section, savedAnswers = answers) => {
      const items = flattenSectionQuestions(section);
      const firstUnanswered = items.findIndex(({ question }) => savedAnswers[question.id] == null);
      return firstUnanswered >= 0 ? firstUnanswered : 0;
    },
    [answers]
  );

  const enterSectionMenu = useCallback(
    (paper, resumeDoc, limit) => {
      flushActiveQuestionTiming();
      const timings = timingsFromResumeDoc(resumeDoc);
      questionTimingsRef.current = timings;
      setQuestionTimings(timings);
      setActivePaper(paper);
      setActiveSectionId(null);
      setTimerExpired(false);
      setPhase('sectionMenu');
      setQuestionIndex(0);
      setAnswers(
        resumeDoc?.answers && typeof resumeDoc.answers === 'object' ? resumeDoc.answers : {}
      );
      setRemainingSeconds(
        typeof resumeDoc?.remainingSeconds === 'number' ? resumeDoc.remainingSeconds : limit
      );
    },
    [flushActiveQuestionTiming]
  );

  const enterQuestionsPhase = useCallback(
    (paper, sectionId, resumeDoc, limit) => {
      const section = getSectionById(paper, sectionId);
      if (!section) return false;

      flushActiveQuestionTiming();
      const savedAnswers =
        resumeDoc?.answers && typeof resumeDoc.answers === 'object' ? resumeDoc.answers : {};
      const timings = timingsFromResumeDoc(resumeDoc);
      questionTimingsRef.current = timings;
      setQuestionTimings(timings);
      setActivePaper(paper);
      setActiveSectionId(sectionId);
      setAnswers(savedAnswers);
      setRemainingSeconds(
        typeof resumeDoc?.remainingSeconds === 'number' ? resumeDoc.remainingSeconds : limit
      );
      setTimerExpired(false);

      const items = flattenSectionQuestions(section);
      const resumeIdx =
        typeof resumeDoc?.questionIndex === 'number' ? resumeDoc.questionIndex : 0;
      const firstUnanswered = items.findIndex(
        ({ question }) => savedAnswers[question.id] == null
      );
      const startIdx =
        resumeDoc && typeof resumeDoc.questionIndex === 'number'
          ? Math.min(Math.max(0, resumeIdx), Math.max(0, items.length - 1))
          : firstUnanswered >= 0
            ? firstUnanswered
            : 0;
      setQuestionIndex(startIdx);
      setPhase('questions');
      return true;
    },
    [flushActiveQuestionTiming]
  );

  const goToSectionQuestions = useCallback(
    (sectionId, { paper = null, questionIndex: startIndex = null } = {}) => {
      const targetPaper = paper || activePaperRef.current;
      if (!targetPaper) return;
      const section = getSectionById(targetPaper, sectionId);
      if (!section) return;
      const idx = startIndex != null ? startIndex : firstUnansweredInSection(section);
      enterQuestionsPhase(
        targetPaper,
        sectionId,
        {
          answers: answersRef.current,
          questionIndex: idx,
          remainingSeconds: remainingSecondsRef.current,
          questionTimings: snapshotTimingsForSave()
        },
        remainingSecondsRef.current
      );
    },
    [enterQuestionsPhase, firstUnansweredInSection, snapshotTimingsForSave]
  );

  const enterPaper = useCallback(
    (paper, resumeDoc = null) => {
      if (paper.comingSoon) return;
      setChapterResult(null);

      const limit = defaultLimit(paper);
      const savedAnswers =
        resumeDoc?.answers && typeof resumeDoc.answers === 'object' ? resumeDoc.answers : {};
      const timings = resumeDoc ? timingsFromResumeDoc(resumeDoc) : {};

      questionTimingsRef.current = timings;
      setQuestionTimings(timings);
      setActivePaper(paper);
      setActiveSectionId(null);
      setPendingResume(null);
      setAnswers(savedAnswers);
      setQuestionIndex(
        typeof resumeDoc?.questionIndex === 'number' ? resumeDoc.questionIndex : 0
      );
      setRemainingSeconds(
        typeof resumeDoc?.remainingSeconds === 'number' ? resumeDoc.remainingSeconds : limit
      );
      setTimerExpired(false);

      const resumePayload = {
        answers: savedAnswers,
        questionIndex:
          typeof resumeDoc?.questionIndex === 'number' ? resumeDoc.questionIndex : 0,
        remainingSeconds:
          typeof resumeDoc?.remainingSeconds === 'number' ? resumeDoc.remainingSeconds : limit,
        questionTimings: timings,
        activeSectionId: resumeDoc?.activeSectionId ?? null
      };

      if (
        resumeDoc?.resumePhase === 'questions' ||
        resumeDoc?.resumePhase === 'sectionCover'
      ) {
        const sectionId =
          resumeDoc.activeSectionId || getSectionsWithQuestions(paper)[0]?.id || getPrimarySection(paper)?.id;
        if (
          sectionId &&
          enterQuestionsPhase(paper, sectionId, resumePayload, resumePayload.remainingSeconds)
        ) {
          return;
        }
      }

      if (paperUsesSectionMenu(paper)) {
        enterSectionMenu(paper, resumeDoc, limit);
        return;
      }

      const sectionId =
        resumeDoc?.activeSectionId ||
        getSectionsWithQuestions(paper)[0]?.id ||
        getPrimarySection(paper)?.id;
      if (sectionId) {
        enterQuestionsPhase(
          paper,
          sectionId,
          resumeDoc || { answers: {}, remainingSeconds: limit, questionTimings: {} },
          limit
        );
      }
    },
    [enterQuestionsPhase, enterSectionMenu]
  );

  const openPaper = enterPaper;

  const beginSuite = () => {
    setPhase('menu');
  };

  const openSection = (sectionId) => {
    if (!activePaper) return;
    const section = getSectionById(activePaper, sectionId);
    if (!section) return;
    enterQuestionsPhase(
      activePaper,
      sectionId,
      {
        answers,
        questionIndex: firstUnansweredInSection(section),
        remainingSeconds,
        questionTimings: snapshotTimingsForSave()
      },
      remainingSeconds
    );
  };

  const goToNextSection = () => {
    if (!activePaper || !activeSectionId) return;
    const next = getNextSection(activePaper, activeSectionId);
    if (!next) return;
    goToSectionQuestions(next.id, { questionIndex: 0 });
  };

  useEffect(() => {
    if (phase !== 'sectionMenu' || !activePaper || paperUsesSectionMenu(activePaper)) return;
    const sectionId = getPrimarySection(activePaper)?.id;
    if (!sectionId) return;
    enterQuestionsPhase(
      activePaper,
      sectionId,
      {
        answers: answersRef.current,
        questionIndex: 0,
        remainingSeconds: remainingSecondsRef.current,
        questionTimings: snapshotTimingsForSave()
      },
      remainingSecondsRef.current
    );
  }, [phase, activePaper, enterQuestionsPhase, snapshotTimingsForSave]);

  const pauseToSectionMenu = async () => {
    if (!activePaper) return;
    if (!paperUsesSectionMenu(activePaper)) {
      await pauseToMenu();
      return;
    }
    const slug = activePaper.slug;
    const timings = flushAndGetTimings();
    if (hasToken()) {
      await persistProgress(
        slug,
        buildProgressPayload({
          answers,
          questionIndex: 0,
          remainingSeconds,
          resumePhase: 'sectionMenu',
          activeSectionId: null,
          questionTimings: timings
        })
      );
    }
    setActiveSectionId(null);
    setQuestionIndex(0);
    setPhase('sectionMenu');
  };

  const pauseToMenu = async () => {
    if (!activePaper) return;
    const slug = activePaper.slug;
    const timings = flushAndGetTimings();
    if (hasToken()) {
      await persistProgress(
        slug,
        buildProgressPayload({
          answers,
          questionIndex: 0,
          remainingSeconds,
          resumePhase: 'sectionMenu',
          activeSectionId: null,
          questionTimings: timings
        })
      );
    }
    setActivePaper(null);
    setActiveSectionId(null);
    setPhase('menu');
    setQuestionIndex(0);
    setAnswers({});
    setQuestionTimings({});
    questionTimingsRef.current = {};
    setRemainingSeconds(0);
    setTimerExpired(false);
  };

  const handleResume = (paper) => {
    const doc = progressBySlug[paper.slug];
    if (doc) openPaper(paper, doc);
  };

  const selectPaper = (paper) => {
    const doc = progressBySlug[paper.slug];
    if (doc) openPaper(paper, doc);
    else openPaper(paper, null);
  };

  const handleStartNew = async (paper) => {
    if (!(await confirmAsync(t('test.confirmStartNew')))) return;
    ensureAbilitySuiteAttemptId();
    if (hasToken()) {
      try {
        await clearTestProgress(paper.slug, PAPER_TYPE);
        await loadProgressList();
      } catch (e) {
        console.error(e);
      }
    }
    openPaper(paper, null);
  };

  const retakeAbilityTest = useCallback(
    async (paperList, onClearCompletions, options = {}) => {
      const playable = getPlayableAbilityPapers(paperList);
      if (!playable.length) return;
      const confirmKey = options.confirmKey || 'test.confirmRetakeAbilityTest';
      if (!(await confirmAsync(t(confirmKey)))) return;
      startNewAbilitySuiteAttempt();
      onClearCompletions?.();
      if (hasToken()) {
        try {
          await clearAllAbilityProgress();
          await loadProgressList();
        } catch (e) {
          console.error(e);
        }
      }
      openPaper(playable[0], null);
    },
    [confirmAsync, t, loadProgressList, openPaper]
  );

  useEffect(() => {
    const timerActive = phase === 'questions' || phase === 'sectionMenu';
    if (!timerActive) return undefined;
    const id = window.setInterval(() => {
      setRemainingSeconds((r) => {
        if (r <= 1) {
          setTimerExpired(true);
          void finalizeChapterSubmissionRef.current('timed_out');
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, loadProgressList]);

  const activePaperSlug = activePaper?.slug;

  useEffect(() => {
    const shouldAutosave =
      (phase === 'questions' || phase === 'sectionMenu') &&
      activePaperSlug &&
      hasToken();
    if (!shouldAutosave) return undefined;
    const id = window.setTimeout(() => {
      saveTestProgress(
        activePaperSlug,
        buildProgressPayload({
          answers,
          questionIndex: phase === 'questions' ? questionIndex : 0,
          remainingSeconds: remainingSecondsRef.current,
          resumePhase: phase === 'questions' ? 'questions' : 'sectionMenu',
          activeSectionId: phase === 'questions' ? activeSectionId : null,
          questionTimings: snapshotTimingsForSave()
        })
      ).catch(console.error);
    }, 2000);
    return () => clearTimeout(id);
  }, [answers, questionIndex, phase, activePaperSlug, activeSectionId, snapshotTimingsForSave]);

  const selectOption = useCallback((questionId, optionKey) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }));
  }, []);

  const goNext = () => {
    if (questionIndex < totalQuestions - 1) {
      setQuestionIndex((i) => i + 1);
    }
  };

  const goPrev = () => {
    if (questionIndex > 0) {
      setQuestionIndex((i) => i - 1);
    }
  };

  const finishSection = async () => {
    if (!activePaper) return;
    if (paperUsesSectionMenu(activePaper)) {
      await pauseToSectionMenu();
      return;
    }
    if (activeSectionId && getNextSection(activePaper, activeSectionId)) {
      goToNextSection();
      return;
    }
    await submitEntireTest();
  };

  const submitEntireTest = async () => {
    if (!activePaper) return;
    await finalizeChapterSubmission('submitted');
  };

  const currentPassage = currentItem
    ? findPassageInSection(currentItem.section, currentItem.question.passageId)
    : null;

  const usesSectionMenu = activePaper ? paperUsesSectionMenu(activePaper) : false;
  const sectionsWithQuestions = useMemo(
    () => (activePaper ? getSectionsWithQuestions(activePaper) : []),
    [activePaper]
  );
  const activeSectionIndex = activePaper && activeSectionId
    ? getSectionIndex(activePaper, activeSectionId)
    : -1;
  const hasNextSection = Boolean(
    activePaper && activeSectionId && getNextSection(activePaper, activeSectionId)
  );
  const isPenultimateSection =
    sectionsWithQuestions.length >= 2 &&
    activeSectionIndex >= 0 &&
    activeSectionIndex === sectionsWithQuestions.length - 2;
  const sectionQuestionCount = activeSection
    ? (activeSection.questions || []).length
    : 0;
  const showSectionTitle = Boolean(
    currentItem &&
      phase === 'questions' &&
      (usesSectionMenu
        ? !prevItem || prevItem.section.id !== currentItem.section.id
        : questionIndex === 0)
  );
  const paperTitle = activePaper ? pickLocalized(activePaper.title, lang) : '';
  const paperInstruction = activePaper ? pickLocalized(activePaper.paperInstruction, lang) : '';
  const sectionTitle = activeSection ? pickLocalized(activeSection.title, lang) : '';
  const isLast = questionIndex === totalQuestions - 1;
  const currentAnswered = currentItem && answers[currentItem.question.id] != null;

  useEffect(() => {
    if (phase !== 'questions' || !activeSectionId) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [phase, activeSectionId]);

  const selectOptionRef = useRef(selectOption);
  selectOptionRef.current = selectOption;

  const pipelineNavRef = useRef({
    goPrev: () => {},
    goNext: () => {},
    finishSection: async () => {},
    goToNextSection: () => {},
    questionIndex: 0,
    currentAnswered: false,
    isLast: false,
    hasNextSection: false
  });
  pipelineNavRef.current = {
    goPrev,
    goNext,
    finishSection,
    goToNextSection,
    questionIndex,
    currentAnswered,
    isLast,
    hasNextSection
  };

  const abilityExitRef = useRef({
    phase: 'suiteCover',
    pauseToSectionMenu: async () => {},
    pauseToMenu: async () => {},
    resetSession: () => {},
    beginSuite: () => {}
  });
  abilityExitRef.current = { phase, pauseToSectionMenu, pauseToMenu, resetSession, beginSuite };

  useEffect(() => {
    if (phase === 'menu') return undefined;

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isConfirmOpen()) return;
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (target.closest('nav') || target.closest('footer')) return;
      const inMain = target.closest(`#${APP_MAIN_CONTENT_ID}`);
      if (!inMain && target !== document.body && target !== document.documentElement) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      void (async () => {
        const {
          phase: ph,
          pauseToSectionMenu: ptsm,
          pauseToMenu: ptm,
          resetSession: rs,
          beginSuite: bs
        } = abilityExitRef.current;
        if (ph === 'questions') {
          const paper = activePaperRef.current;
          if (paper && !paperUsesSectionMenu(paper)) {
            if (!(await confirmAsync(t('test.confirmPauseToMenu')))) return;
            void ptm();
          } else {
            if (!(await confirmAsync(t('test.confirmBackToSections')))) return;
            void ptsm();
          }
        } else if (ph === 'sectionMenu') {
          if (!(await confirmAsync(t('test.confirmExitToMenu')))) return;
          void ptm();
        } else if (ph === 'suiteCover') {
          bs();
        } else {
          if (!(await confirmAsync(t('test.confirmBackToPapers')))) return;
          rs();
        }
      })();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase, t, confirmAsync, isConfirmOpen]);

  useEffect(() => {
    if (phase !== 'questions' || !currentItem?.question?.options?.length) {
      return undefined;
    }

    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isConfirmOpen()) return;

      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
      if (target.closest('nav') || target.closest('footer')) return;

      const inMain = target.closest(`#${APP_MAIN_CONTENT_ID}`);
      if (!inMain && target !== document.body && target !== document.documentElement) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        const { questionIndex: qi, goPrev: prev } = pipelineNavRef.current;
        if (qi > 0) {
          e.preventDefault();
          prev();
        }
        return;
      }

      const advanceIfAnswered = () => {
        const {
          currentAnswered: answered,
          isLast: last,
          hasNextSection: hasNext,
          goNext: next,
          finishSection: finish,
          goToNextSection: nextSection
        } = pipelineNavRef.current;
        if (!answered) return false;
        if (last) {
          if (hasNext) nextSection();
          else finish();
        } else next();
        return true;
      };

      if (e.key === 'ArrowRight') {
        if (advanceIfAnswered()) e.preventDefault();
        return;
      }

      if (e.key === 'Enter') {
        if (e.isComposing) return;
        if (target.closest('button, a[href], [role="button"]')) return;
        if (advanceIfAnswered()) e.preventDefault();
        return;
      }

      const digit = digitFromKeyboardEvent(e);
      if (digit == null) return;

      const opts = currentItem.question.options;
      const idx = digit - 1;
      if (idx >= opts.length) return;

      e.preventDefault();
      selectOptionRef.current(currentItem.question.id, opts[idx].key);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase, currentItem, isConfirmOpen]);

  return {
    t,
    lang,
    confirmAsync,
    ConfirmDialog,
    activePaper,
    activeSectionId,
    phase,
    questionIndex,
    answers,
    remainingSeconds,
    timerExpired,
    chapterResult,
    progressBySlug,
    totalQuestions,
    totalPaperQuestions,
    paperQuestionIndex,
    answeredInPaper,
    allQuestionsAnswered,
    currentItem,
    defaultLimit,
    openPaper,
    enterPaper,
    selectPaper,
    handleResume,
    handleStartNew,
    retakeAbilityTest,
    resetSession,
    beginSuite,
    openSection,
    goToNextSection,
    pauseToSectionMenu,
    pauseToMenu,
    finishSection,
    submitEntireTest,
    paperTitle,
    paperInstruction,
    sectionTitle,
    showSectionTitle,
    usesSectionMenu,
    sectionsWithQuestions,
    activeSectionIndex,
    hasNextSection,
    isPenultimateSection,
    sectionQuestionCount,
    pendingResume,
    currentPassage,
    selectOption,
    goPrev,
    goNext,
    isLast,
    currentAnswered
  };
}
